import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Package, Save, Upload, FileText, X, Lock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const NouvellDemandeEPI = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [typesEPI, setTypesEPI] = useState([]);
  const [searchCollab, setSearchCollab] = useState('');
  const [formData, setFormData] = useState({
    collaborateur_id: '',
    type_epi_id: '',
    commentaire: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);

  const loadCollaborateurs = useCallback(async () => {
    try {
      const response = await api.get('/collaborateurs?actif=true');
      // L'API renvoie un tableau directement
      setCollaborateurs(Array.isArray(response) ? response : (response.collaborateurs || []));
    } catch (error) {
      console.error('Error loading collaborateurs:', error);
      toast.error('Erreur lors du chargement des collaborateurs');
    }
  }, []);

  const loadTypesEPI = useCallback(async () => {
    try {
      const response = await api.get('/types-epi-specifiques?actif=true');
      setTypesEPI(response.types_epi || []);
    } catch (error) {
      console.error('Error loading types EPI:', error);
      toast.error('Erreur lors du chargement des types d\'EPI');
    }
  }, []);

  useEffect(() => {
    loadCollaborateurs();
    loadTypesEPI();
  }, [loadCollaborateurs, loadTypesEPI]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.collaborateur_id) {
      toast.error('Veuillez sélectionner un collaborateur');
      return;
    }
    if (!formData.type_epi_id) {
      toast.error('Veuillez sélectionner un type d\'EPI');
      return;
    }

    setLoading(true);
    try {
      // Créer la demande
      const response = await api.post('/demandes-epi', formData);
      const demandeId = response.demande?.id;

      // Uploader les documents si présents
      if (selectedFiles.length > 0 && demandeId) {
        const token = localStorage.getItem('access_token');
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        for (const fileData of selectedFiles) {
          const uploadFormData = new FormData();
          uploadFormData.append('file', fileData.file);
          if (fileData.description) {
            uploadFormData.append('description', fileData.description);
          }

          try {
            await fetch(`${API_URL}/api/demandes-epi/${demandeId}/documents`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: uploadFormData
            });
          } catch (uploadError) {
            console.error('Error uploading document:', uploadError);
            toast.error(`Erreur lors de l'upload de ${fileData.file.name}`);
          }
        }
      }

      toast.success('Demande d\'EPI créée avec succès');
      navigate('/epi/demandes');
    } catch (error) {
      console.error('Error creating demande:', error);
      toast.error(error.detail || 'Erreur lors de la création de la demande');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx'];
    
    const validFiles = files.filter(file => {
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExt)) {
        toast.error(`Type de fichier non autorisé: ${file.name}`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Fichier trop volumineux: ${file.name} (max 10 MB)`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [
      ...prev,
      ...validFiles.map(file => ({ file, description: '' }))
    ]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateFileDescription = (index, description) => {
    setSelectedFiles(prev => prev.map((f, i) => 
      i === index ? { ...f, description } : f
    ));
  };

  const filteredCollaborateurs = collaborateurs.filter(c =>
    `${c.prenom} ${c.nom} ${c.matricule}`.toLowerCase().includes(searchCollab.toLowerCase())
  );

  const selectedCollab = collaborateurs.find(c => c.id === formData.collaborateur_id);
  const selectedType = typesEPI.find(t => t.id === formData.type_epi_id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-8 w-8 text-blue-600" />
            Nouvelle Demande HSE
          </h1>
          <p className="text-gray-600 mt-2">
            Créer une demande d'équipement de protection individuelle
          </p>
        </div>
        <Button
          onClick={() => navigate('/epi/demandes')}
          variant="outline"
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Informations de la demande</CardTitle>
            <CardDescription>
              Remplissez les informations pour créer une demande HSE
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sélection Collaborateur */}
            <div className="space-y-2">
              <Label htmlFor="collaborateur">Collaborateur *</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Rechercher un collaborateur..."
                  value={searchCollab}
                  onChange={(e) => setSearchCollab(e.target.value)}
                />
                <Select
                  value={formData.collaborateur_id}
                  onValueChange={(value) => setFormData({ ...formData, collaborateur_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCollaborateurs.map((collab) => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.prenom} {collab.nom} ({collab.matricule})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCollab && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Collaborateur sélectionné:</strong> {selectedCollab.prenom} {selectedCollab.nom}
                    </p>
                    <p className="text-sm text-blue-600">Matricule: {selectedCollab.matricule}</p>
                    {selectedCollab.secteur && (
                      <p className="text-sm text-blue-600">Secteur: {selectedCollab.secteur}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Type d'EPI */}
            <div className="space-y-2">
              <Label htmlFor="type_epi">Type d'EPI *</Label>
              <Select
                value={formData.type_epi_id}
                onValueChange={(value) => setFormData({ ...formData, type_epi_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un type d'EPI" />
                </SelectTrigger>
                <SelectContent>
                  {typesEPI.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>{selectedType.nom}</strong>
                  </p>
                  {selectedType.description && (
                    <p className="text-sm text-green-600 mt-1">{selectedType.description}</p>
                  )}
                  <p className="text-sm text-green-600 mt-2">
                    Durée de validité: <strong>{selectedType.duree_validite_mois} mois</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Commentaire */}
            <div className="space-y-2">
              <Label htmlFor="commentaire">Commentaire (optionnel)</Label>
              <Textarea
                id="commentaire"
                placeholder="Informations complémentaires sur la demande..."
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                rows={4}
              />
            </div>

            {/* Documents joints */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label>Documents joints (optionnel)</Label>
                <Badge className="bg-amber-100 text-amber-800 gap-1">
                  <Lock className="h-3 w-3" />
                  Confidentiel
                </Badge>
              </div>
              <p className="text-xs text-gray-500">
                Les documents sont accessibles uniquement aux Managers, HSE et RH
              </p>
              
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  multiple
                  className="hidden"
                  id="file-upload"
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Ajouter des documents
                  </Button>
                  <span className="text-xs text-gray-500">
                    PDF, Images, Word, Excel (max 10 MB par fichier)
                  </span>
                </div>
              </div>

              {/* Liste des fichiers sélectionnés */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  {selectedFiles.map((fileData, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <FileText className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0 space-y-2">
                        <p className="font-medium text-sm truncate">{fileData.file.name}</p>
                        <Input
                          placeholder="Description du document (optionnel)"
                          value={fileData.description}
                          onChange={(e) => updateFileDescription(index, e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="gap-2"
                data-testid="btn-submit"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Création en cours...' : 'Créer la demande'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/epi/demandes')}
                disabled={loading}
              >
                Annuler
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default NouvellDemandeEPI;